import React from 'react';
import { Form, Input, InputNumber, Select, DatePicker, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { AttributeDefinition } from '@components/operator/shared/inventory_verification/types';

interface CustomAttributeRendererProps {
  attributeDefinitions: AttributeDefinition[];
  disabled?: boolean;
}

const CustomAttributeRenderer: React.FC<CustomAttributeRendererProps> = ({
  attributeDefinitions, disabled,
}) => {
  const { t } = useTranslation();

  if (!attributeDefinitions || attributeDefinitions.length === 0) return null;

  const renderInput = (def: AttributeDefinition) => {
    const commonProps = {
      placeholder: def.data_type === 'select'
        ? (t('common.placeholder.select', { defaultValue: 'Please select' }) as string)
        : '',
      style: { width: '100%' },
      disabled,
    };

    if (def.data_type === 'select') {
      return (
        <Select
          {...commonProps}
          allowClear={!def.required}
          options={(def.options || []).map(opt => ({ label: opt, value: opt }))}
        />
      );
    }
    if (def.data_type === 'number') return <InputNumber {...commonProps} />;
    if (def.data_type === 'date') return <DatePicker {...commonProps} format="YYYY-MM-DD" />;
    if (def.data_type === 'boolean') {
      // Boolean is always binary; Schema 層 (InventoryAttributeDefinition)
      // 已禁止 boolean + required=true，因此這裡永遠用 Switch。
      return <Switch disabled={disabled} />;
    }
    return <Input {...commonProps} />;
  };

  return (
    <>
      {attributeDefinitions.map((def) => {
        // Schema 層 (InventoryAttributeDefinition) 已禁 boolean+required=true，
        // boolean 永遠用 Switch（`checked` + implicit false default）。
        const isBoolean = def.data_type === 'boolean';
        return (
          <Form.Item
            key={def.id}
            name={['custom_attributes', def.name]}
            label={def.label}
            rules={def.required ? [{ required: true, message: t('common.validation.required', { defaultValue: 'Required' }) as string }] : undefined}
            valuePropName={isBoolean ? 'checked' : 'value'}
            initialValue={isBoolean ? false : undefined}
            getValueProps={(value) => {
              if (def.data_type === 'date' && value) return { value: dayjs(value as string) };
              if (def.data_type === 'boolean') return { checked: !!value };
              return { value };
            }}
            getValueFromEvent={(event: unknown) => {
              if (def.data_type === 'date' && event) {
                return (event as dayjs.Dayjs).format('YYYY-MM-DD');
              }
              if (def.data_type === 'boolean') {
                return !!event;   // Switch onChange → boolean
              }
              if ((event as { target?: { value?: unknown } })?.target) {
                return (event as { target: { value: unknown } }).target.value;
              }
              return event;
            }}
          >
            {renderInput(def)}
          </Form.Item>
        );
      })}
    </>
  );
};

export default CustomAttributeRenderer;
